import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAnalytics } from '@/lib/analytics';

// Form validation schema
const emailPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type EmailPasswordFormData = z.infer<typeof emailPasswordSchema>;

interface EmailPasswordFormProps {
  mode: 'login' | 'signup';
  onSuccess?: () => void;
  inviteId?: string;
  claimBrandId?: string;
}

export function EmailPasswordForm({ mode, onSuccess, inviteId, claimBrandId }: EmailPasswordFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { track } = useAnalytics();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailPasswordFormData>({
    resolver: zodResolver(emailPasswordSchema),
  });

  const onSubmit = async (data: EmailPasswordFormData) => {
    setIsSubmitting(true);

    try {
      if (mode === 'signup') {
        // Sign up with email and password
        const { data: authData, error } = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            data: {
              invite_id: inviteId,
              claim_brand_id: claimBrandId,
            },
          },
        });

        if (error) throw error;

        // Track signup event
        track({
          name: 'user_signed_up',
          properties: {
            method: 'password',
            email: data.email,
          }
        });

        toast({
          title: 'Account created!',
          description: 'Please check your email to verify your account.',
        });

        // If user is already verified (some Supabase configs), navigate to onboarding
        if (authData.user && authData.session) {
          navigate('/onboarding');
        }
      } else {
        // Sign in with email and password
        const { data: authData, error } = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        });

        if (error) throw error;

        // Track login event
        track({
          name: 'user_logged_in',
          properties: {
            method: 'password',
            email: data.email,
          }
        });

        toast({
          title: 'Welcome back!',
          description: 'You have successfully logged in.',
        });

        // Check if user needs onboarding
        if (authData.user) {
          const { data: profile } = await supabase
            .schema('marketing_and_customer_success')
            .from('profiles')
            .select('onboarding_completed')
            .eq('id', authData.user.id)
            .single();

          if (profile && !profile.onboarding_completed) {
            navigate('/onboarding');
          } else {
            navigate('/dashboard');
          }
        }
      }

      onSuccess?.();
    } catch (error: any) {
      console.error('Authentication error:', error);
      
      // Handle specific error cases
      let errorMessage = 'An error occurred during authentication';
      
      if (error.message?.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password';
      } else if (error.message?.includes('Email not confirmed')) {
        errorMessage = 'Please verify your email before logging in';
      } else if (error.message?.includes('User already registered')) {
        errorMessage = 'An account with this email already exists';
      }

      toast({
        title: 'Authentication failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          {...register('email')}
          id="email"
          type="email"
          placeholder="you@example.com"
          autoComplete="email"
          disabled={isSubmitting}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            {...register('password')}
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            disabled={isSubmitting}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isSubmitting}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        {errors.password && (
          <p className="text-sm text-destructive">{errors.password.message}</p>
        )}
        {mode === 'signup' && (
          <p className="text-xs text-muted-foreground">
            Must be at least 8 characters long
          </p>
        )}
      </div>

      {mode === 'login' && (
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="link"
            className="px-0 font-normal"
            onClick={() => navigate('/reset-password')}
          >
            Forgot your password?
          </Button>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {mode === 'signup' ? 'Create account' : 'Sign in'}
      </Button>
    </form>
  );
}